/**
 * Fetches and summarises org data from Firestore for BespokeAI context injection.
 * Keeps payloads lean — only the fields Claude needs to reason about.
 */
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../utils/firebase';

const rootCol = (col) => collection(db, col);

const toArr = (snap) => snap.docs.map(d => ({ id: d.id, ...d.data() }));

const daysSince = (ts) => {
  if (!ts) return null;
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return Math.floor((Date.now() - d.getTime()) / 86400000);
};

const fmtDate = (ts) => {
  if (!ts) return null;
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-SG', { day: '2-digit', month: 'short', year: 'numeric' });
};

// ── Members ──────────────────────────────────────────────────────────────────

const fetchMembers = async (orgId) => {
  const snap = await getDocs(query(rootCol('members'), where('orgId', '==', orgId)));
  return toArr(snap).map(m => ({
    name:               m.name || m.fullName || 'Unknown',
    status:             m.status || 'active',
    engagementTier:     m.engagementTier || 'low',
    engagementScore:    m.engagementScore ?? 0,
    eventsAttended:     m.eventsAttendedCount || 0,
    noShows:            m.noShowCount || 0,
    daysSinceActive:    daysSince(m.lastActiveAt),
    dateJoined:         m.dateJoined || fmtDate(m.createdAt),
    tags:               (m.tags || []).join(', '),
    widowStage:         m.widowStage || null,
    employmentStatus:   m.employmentStatus || null,
  }));
};

// ── Volunteers ───────────────────────────────────────────────────────────────

const fetchVolunteers = async (orgId) => {
  const snap = await getDocs(query(rootCol('volunteers'), where('orgId', '==', orgId)));
  return toArr(snap).map(v => ({
    name:         v.name || v.fullName || 'Unknown',
    status:       v.status || 'active',
    totalHours:   v.totalHours || 0,
    skills:       (v.skills || []).join(', '),
    roles:        (v.volunteerRoles || []).join(', '),
    shiftsCount:  v.shiftsCount || 0,
    dateJoined:   v.dateJoined || fmtDate(v.createdAt),
  }));
};

// ── Events ───────────────────────────────────────────────────────────────────

const fetchEvents = async (orgId) => {
  const snap = await getDocs(query(rootCol('events'), where('orgId', '==', orgId)));
  return toArr(snap)
    .filter(e => e.record_type !== 'proposal')
    .map(e => ({
      title:         e.title || e.name || 'Untitled',
      status:        e.status,
      category:      e.category || '',
      date:          fmtDate(e.start_datetime),
      memberCap:     e.member_capacity ?? null,
      registered:    e.registeredCount || e.registration_count || 0,
      attended:      e.attendedCount || 0,
      noShows:       e.noShowCount || 0,
    }));
};

// ── Per-module context builders ───────────────────────────────────────────────

export const fetchCommunityContext = async (orgId) => {
  const [members, volunteers] = await Promise.all([
    fetchMembers(orgId),
    fetchVolunteers(orgId),
  ]);
  return { members, volunteers };
};

export const fetchEventsContext = async (orgId) => {
  const events = await fetchEvents(orgId);
  return { events };
};

export const fetchReportsContext = async (orgId) => {
  const [members, volunteers, events] = await Promise.all([
    fetchMembers(orgId),
    fetchVolunteers(orgId),
    fetchEvents(orgId),
  ]);
  return { members, volunteers, events };
};

export const fetchCommunicationsContext = async (orgId) => {
  const [members, volunteers, events] = await Promise.all([
    fetchMembers(orgId),
    fetchVolunteers(orgId),
    fetchEvents(orgId),
  ]);
  return { members, volunteers, events };
};
